import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * CommentComposer + CommentList — IG-style lightweight conversation on a
 * seed. Distinct from Field Notes (structured after-use reviews).
 *
 *   [ avatar ]  Add a comment…                          [ Post ]
 *   ─────────────────────────────────────
 *   holly_marie_designs  This looks so good  2h
 *   kelly_c  @holly right? make it this weekend  1h
 *
 * Rules:
 *   - Any signed-in user can comment. Anon viewers see a soft prompt.
 *   - 800 char cap enforced server-side, softly enforced client-side.
 *   - @mentions render in olive; server resolves them to user_ids.
 *   - Author of a comment can delete it; seed author can also delete
 *     anyone's comment on their seed.
 */

// Render text with @mentions styled. Server accepts a plain string; we just
// visually highlight the tokens so people see the mention while typing/
// reading. Full autocomplete is a follow-up.
const MENTION_RE = /(^|[\s(])@([A-Za-z0-9][A-Za-z0-9._-]{0,29})/g;
function renderMentions(text) {
  const out = [];
  let last = 0;
  let m;
  const re = new RegExp(MENTION_RE);
  while ((m = re.exec(text)) !== null) {
    const before = text.slice(last, m.index + m[1].length);
    if (before) out.push(before);
    out.push(
      <span key={m.index} className="text-[#4E5A3D] font-medium">
        @{m[2]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  const tail = text.slice(last);
  if (tail) out.push(tail);
  return out;
}

function timeShort(iso) {
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: false })
      .replace(" seconds", "s").replace(" second", "s")
      .replace(" minutes", "m").replace(" minute", "m")
      .replace(" hours", "h").replace(" hour", "h")
      .replace(" days", "d").replace(" day", "d")
      .replace(" months", "mo").replace(" month", "mo")
      .replace(" years", "y").replace(" year", "y");
  } catch { return ""; }
}

export default function CommentThread({ ideaId, seedAuthorId, openSignal }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [count, setCount] = useState(0);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ideas/${ideaId}/comments`);
      setComments(data.comments || []);
      setCount(data.count || 0);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ideaId]);

  // When the parent bumps openSignal (comment icon tapped in the action row),
  // scroll into view + focus the composer so users can start typing right away.
  useEffect(() => {
    if (!openSignal) return;
    if (inputRef.current) {
      inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      // brief delay so the smooth scroll finishes before focus
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [openSignal]);

  const post = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (trimmed.length > 800) return toast("Comment is a little long — 800 characters max.");
    setPosting(true);
    try {
      const { data } = await api.post(`/ideas/${ideaId}/comments`, { text: trimmed });
      setComments((prev) => [...prev, data]);
      setCount((c) => c + 1);
      setText("");
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't post the comment.");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    const snapshot = comments;
    setComments((prev) => prev.filter((x) => x.id !== c.id));
    setCount((n) => Math.max(0, n - 1));
    try {
      await api.delete(`/comments/${c.id}`);
    } catch (err) {
      setComments(snapshot);
      setCount((n) => n + 1);
      toast(err?.response?.data?.detail || "Couldn't delete.");
    }
  };

  return (
    <section
      data-testid="comment-thread"
      className="mt-6"
    >
      {/* Header — makes the distinction from Field Notes explicit. Comments
          are for questions + chit-chat BEFORE you try something; Field
          Notes are structured reviews AFTER you try it. */}
      <div className="mb-4">
        <h3 className="font-serif-display text-2xl text-[#2A2823]" data-testid="comment-heading">
          Have a question?
        </h3>
        <p className="text-sm text-[#2A2823]/60 mt-1">
          Ask the person who planted this seed. Reviews after you've actually tried it belong in{" "}
          <span className="font-serif-display italic">Field Notes</span> below.
        </p>
      </div>

      {count > 0 && (
        <p data-testid="comment-count" className="text-xs text-[#2A2823]/55 mb-3 font-serif-display italic">
          {count === 1 ? "1 question or comment" : `${count} questions & comments`}
        </p>
      )}

      {!loading && comments.length > 0 && (
        <ul className="space-y-3 mb-4">
          {comments.map((c) => {
            const canDelete = user && (user.user_id === c.author_id || user.user_id === seedAuthorId);
            return (
              <li key={c.id} data-testid={`comment-${c.id}`} className="flex gap-3 group">
                {c.author_picture ? (
                  <img
                    src={c.author_picture}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover shrink-0 mt-0.5"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#4E5A3D]/15 text-[#4E5A3D] flex items-center justify-center text-xs font-medium shrink-0 mt-0.5">
                    {(c.author_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#2A2823] leading-snug">
                    <Link
                      to={`/roots/${c.author_id}`}
                      className="font-medium hover:underline decoration-[#B79256]/60"
                    >
                      {c.author_name}
                    </Link>{" "}
                    <span className="break-words">{renderMentions(c.text)}</span>
                  </p>
                  <p className="text-[11px] text-[#2A2823]/45 mt-0.5">
                    {timeShort(c.created_at)} ago
                  </p>
                </div>
                {canDelete && (
                  <button
                    type="button"
                    data-testid={`comment-delete-${c.id}`}
                    onClick={() => remove(c)}
                    aria-label="Delete comment"
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-[#2A2823]/40 hover:text-red-800 p-1"
                    title="Delete comment"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {user ? (
        <div className="flex items-start gap-3 pt-3 border-t border-[#B79256]/25">
          {user.picture ? (
            <img
              src={user.picture}
              alt=""
              className="w-8 h-8 rounded-full object-cover shrink-0 mt-1"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#4E5A3D]/15 text-[#4E5A3D] flex items-center justify-center text-xs font-medium shrink-0 mt-1">
              {(user.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <textarea
              ref={inputRef}
              data-testid="comment-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  post();
                }
              }}
              maxLength={800}
              placeholder="Have a question? Ask here — @name to tag someone"
              rows={2}
              className="w-full resize-none bg-transparent text-sm text-[#2A2823] placeholder:text-[#2A2823]/40 focus:outline-none"
            />
            {text.trim().length > 0 && (
              <div className="flex items-center justify-between mt-1">
                <span className="text-[11px] text-[#2A2823]/40">
                  {text.length}/800 · @-mention to tag someone
                </span>
                <button
                  type="button"
                  data-testid="comment-post-btn"
                  onClick={post}
                  disabled={posting}
                  className="text-sm font-medium text-[#4E5A3D] hover:text-[#3E4830] disabled:opacity-50 transition-colors"
                >
                  {posting ? "Posting…" : "Post"}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-[#2A2823]/60 italic pt-3 border-t border-[#B79256]/25">
          Sign in to leave a comment or tag a friend.
        </p>
      )}
    </section>
  );
}
